import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const TARGET_EMAIL = 'mehaganw@gmail.com';

const HOME_SRC   = 'https://tlphome.aiagentichub.io/widget.js';
const LEARN_SRC  = 'https://tlplearn.aiagentichub.io/widget.js';

const HOME_AGENT_ID  = 'agent_5501m2x4mtnke56t4jtdx77pyrhm';
const LEARN_AGENT_ID = 'agent_8601m2x54wede27t8fjz4w86rhhp';

const ALLOW_PERMISSIONS = 'autoplay *; microphone *; camera *; encrypted-media *; fullscreen *; speaker-selection *';

/* ─────────────────────────────────────────────────────────────
   One AudioContext for the whole page.

   A browser allows only a handful of concurrent AudioContexts -- six in
   Chrome -- and then throws on construction. Creating one per gesture
   exhausted that budget and left none for the widget to play the agent
   through, so the call connected silently. This hands back the same
   instance however many times it is asked for.
───────────────────────────────────────────────────────────── */
let sharedAudioContext = null;
let audioContextUnavailable = false;

function getSharedAudioContext() {
    if (sharedAudioContext) return sharedAudioContext;
    if (audioContextUnavailable) return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) { audioContextUnavailable = true; return null; }
    try {
        sharedAudioContext = new Ctx();
    } catch (_) {
        // Budget already exhausted by something else; stop trying.
        audioContextUnavailable = true;
        return null;
    }
    return sharedAudioContext;
}

/* ─────────────────────────────────────────────────────────────
   Permissions helper for every iframe injected by the widgets
───────────────────────────────────────────────────────────── */
function applyPermissions(iframe) {
    if (!iframe) return;
    try {
        if (iframe.getAttribute('allow') === ALLOW_PERMISSIONS) return;

        // A frame's permissions policy is evaluated when it navigates. If src
        // is already set, setting `allow` now is too late for the document
        // that is loading, so re-point src to make it navigate again under the
        // new policy. Without this the frame is denied autoplay and the
        // agent's voice never reaches the speakers.
        const src = iframe.getAttribute('src');
        iframe.setAttribute('allow', ALLOW_PERMISSIONS);
        if (src && !iframe.dataset.permsReloaded) {
            iframe.dataset.permsReloaded = '1';
            iframe.setAttribute('src', src);
        }
    } catch (_) {}
}

/* ─────────────────────────────────────────────────────────────
   Find the top-level DOM wrapper a widget created.
   The scripts do:
     const div = document.createElement('elevenlabs-convai');
     div.setAttribute('agent-id', '...');
     document.body.appendChild(div);
   So we look for that element in <body>'s direct or shallow children.
───────────────────────────────────────────────────────────── */
function findWidgetEl(agentId) {
    // Try exact attribute match first
    const el = document.body.querySelector(`[agent-id="${agentId}"]`);
    if (el) return el;
    // Fallback: look for elevenlabs-convai elements and check innerHTML / data
    const all = document.body.querySelectorAll('elevenlabs-convai, ai-agent-widget, [class*="elevenlabs"], [id*="elevenlabs"]');
    for (const node of all) {
        if (node.tagName === 'SCRIPT' || node.tagName === 'LINK') continue;
        const outerHtml = (node.outerHTML || '').toLowerCase();
        if (outerHtml.includes(agentId.toLowerCase())) return node;
    }
    return null;
}

function showEl(el) {
    if (!el) return;
    el.style.removeProperty('display');
    el.style.removeProperty('visibility');
    el.style.removeProperty('pointer-events');
}

function hideEl(el) {
    if (!el) return;
    el.style.setProperty('display',         'none',   'important');
    el.style.setProperty('visibility',      'hidden', 'important');
    el.style.setProperty('pointer-events',  'none',   'important');
}

function syncVisibility(isHomePage, isLearnPage) {
    const homeEl  = findWidgetEl(HOME_AGENT_ID);
    const learnEl = findWidgetEl(LEARN_AGENT_ID);

    if (isHomePage) {
        showEl(homeEl);
        hideEl(learnEl);
    } else if (isLearnPage) {
        hideEl(homeEl);
        showEl(learnEl);
    } else {
        hideEl(homeEl);
        hideEl(learnEl);
    }
}

function loadScript(src, id) {
    if (document.getElementById(id)) return;
    const script   = document.createElement('script');
    script.src     = src;
    script.id      = id;
    script.async   = true;
    document.body.appendChild(script);
}

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
export default function ElevenLabsAgentWidget() {
    const { user }    = useAuth();
    const location    = useLocation();

    useEffect(() => {
        const userEmail = (user?.email || '').toLowerCase().trim();

        if (userEmail !== TARGET_EMAIL) {
            // Not the target user — make sure both widgets are hidden if they somehow exist
            hideEl(findWidgetEl(HOME_AGENT_ID));
            hideEl(findWidgetEl(LEARN_AGENT_ID));
            return;
        }

        const path        = location.pathname;
        const isHomePage  = path === '/dashboard' || path === '/';
        const isLearnPage = path.startsWith('/learning') || path.startsWith('/learning-modules');

        // ── Audio context unlocker (needed for voice output) ──
        // Resume one shared context on the first gesture, then stop listening.
        // Never construct a second: the browser's per-page budget is small and
        // the widget needs a slot of its own to play the agent through.
        const unlockAudio = () => {
            const ctx = getSharedAudioContext();
            if (!ctx) { detachUnlock(); return; }
            if (ctx.state === 'suspended') {
                ctx.resume().catch(() => {});
            }
            if (ctx.state === 'running') detachUnlock();
        };
        function detachUnlock() {
            window.removeEventListener('click',      unlockAudio, { capture: true });
            window.removeEventListener('touchstart', unlockAudio, { capture: true });
        }
        window.addEventListener('click',      unlockAudio, { capture: true, passive: true });
        window.addEventListener('touchstart', unlockAudio, { capture: true, passive: true });

        // ── Request mic permission proactively ──
        if (navigator.mediaDevices?.getUserMedia) {
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then(s => s.getTracks().forEach(t => t.stop()))
                .catch(() => {});
        }

        // ── Patch every iframe the widget injects ──
        const observer = new MutationObserver(mutations => {
            mutations.forEach(m => {
                m.addedNodes.forEach(node => {
                    if (node.nodeType !== 1) return;
                    if (node.tagName === 'IFRAME') applyPermissions(node);
                    node.querySelectorAll?.('iframe').forEach(applyPermissions);
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
        document.querySelectorAll('iframe').forEach(applyPermissions);

        // ── Load the script(s) only when needed ──
        if (isHomePage)  loadScript(HOME_SRC,  'elevenlabs-home-script');
        if (isLearnPage) loadScript(LEARN_SRC, 'elevenlabs-learn-script');

        // ── Sync visibility right now and after short delays (widgets load async) ──
        const run = () => syncVisibility(isHomePage, isLearnPage);
        run();
        const t1 = setTimeout(run, 400);
        const t2 = setTimeout(run, 1200);
        const t3 = setTimeout(run, 3000);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            detachUnlock();
            observer.disconnect();
            // On route leave, hide both
            hideEl(findWidgetEl(HOME_AGENT_ID));
            hideEl(findWidgetEl(LEARN_AGENT_ID));
        };
    }, [user, location.pathname]);

    return null;
}
